import axios from 'axios';
import { post, get, patch, del } from '@/shared/api';
import { apiClient, upload } from '@/shared/api/client';
import { getCurrentLanguage } from '@/shared/i18n';
import type {
  LoginRequest,
  LoginSSORequest,
  RegisterRequest,
  AuthResponse,
  RegisterResponse,
  RefreshTokenResponse,
  RecoveryPasswordRequest,
  ResetPasswordRequest,
  VerifyOtpRequest,
  CheckEmailResponse,
  CheckPhoneResponse,
  User,
} from '../types';

export interface VehicleType {
  type: string;
  labelEs: string;
  labelEn: string;
  labelFr: string;
  imageUrl: string;
  sortOrder?: number;
}

/**
 * API endpoints for authentication
 */
const AUTH_ENDPOINTS = {
  LOGIN: '/auth/login',
  LOGIN_SSO: '/auth/login-sso',
  REGISTER: '/auth/createUser',
  REFRESH: '/auth/refresh',
  REVOKE: '/auth/revoke',
  CHECK_EMAIL: '/auth/check-email',
  CHECK_PHONE: '/auth/check-phone',
  REQUEST_OTP: '/otp/request-otp',
  VERIFY_OTP: '/auth/verify-otp',
  VALIDATE_OTP: '/auth/validate-otp',
  RESET_PASSWORD: '/auth/reset-password',
  PROFILE: '/auth/profile',
  PROFILE_AVATAR: '/auth/profile/avatar',
  USER_PROFILE: '/users/user',
  UPDATE_LANGUAGE: '/users/update-language',
  DELETE_ACCOUNT: '/users/delete-account',
} as const;

/**
 * Login with email and password
 */
export async function loginWithCredentials(data: LoginRequest): Promise<AuthResponse> {
  return post<AuthResponse>(AUTH_ENDPOINTS.LOGIN, data);
}

/**
 * Login with SSO provider (Google, Apple)
 */
export async function loginWithSSO(data: LoginSSORequest): Promise<AuthResponse> {
  const response = await apiClient.post<AuthResponse>(
    AUTH_ENDPOINTS.LOGIN_SSO,
    {
      provider: data.provider,
      platform: data.platform === 'web' ? 'android' : data.platform,
    },
    {
      headers: {
        Authorization: `Bearer ${data.idToken}`,
      },
    }
  );
  return response.data;
}

/**
 * Register a new user
 */
export async function registerUser(data: RegisterRequest): Promise<RegisterResponse> {
  return post<RegisterResponse>(AUTH_ENDPOINTS.REGISTER, {
    ...data,
    username: data.username || data.email.toLowerCase(),
  });
}

/**
 * Refresh access token.
 * The refreshToken is sent automatically via the httpOnly cookie (withCredentials).
 * No body is needed — the backend reads the cookie.
 */
export async function refreshToken(): Promise<RefreshTokenResponse> {
  const response = await apiClient.post<RefreshTokenResponse>(
    AUTH_ENDPOINTS.REFRESH,
    {},
    { withCredentials: true }
  );
  return response.data;
}

/**
 * Revoke token (logout on server).
 * Uses plain axios (NOT apiClient) to avoid the response interceptor
 * which would trigger a refresh → logout → revokeToken infinite loop
 * when the token is already expired.
 */
export async function revokeToken(): Promise<void> {
  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
  try {
    await axios.post(
      `${API_BASE_URL}${AUTH_ENDPOINTS.REVOKE}`,
      {},
      { withCredentials: true, timeout: 5_000 }
    );
  } catch {
    // Best-effort — if revoke fails (401, network error), we still proceed with local logout
  }
}

/**
 * Check if email is available
 */
export async function checkEmail(email: string): Promise<CheckEmailResponse> {
  const language = getCurrentLanguage().toLowerCase();
  const lang = language === 'es' || language === 'fr' ? language : 'en';
  const response = await get<{ exists: boolean; message?: string }>(
    `${AUTH_ENDPOINTS.CHECK_EMAIL}/${encodeURIComponent(email)}/${lang}`
  );
  return {
    available: !response.exists,
    message: response.message,
  };
}

/**
 * Check if phone is available
 */
export async function checkPhone(phone: string): Promise<CheckPhoneResponse> {
  try {
    return await get<CheckPhoneResponse>(`${AUTH_ENDPOINTS.CHECK_PHONE}?phone=${encodeURIComponent(phone)}`);
  } catch {
    // Backend may not expose this endpoint in all environments.
    return { available: true };
  }
}

/**
 * Request OTP for password recovery or verification
 */
export async function requestOtp(data: RecoveryPasswordRequest): Promise<{ success: boolean; message: string }> {
  return post(AUTH_ENDPOINTS.REQUEST_OTP, data);
}

/**
 * Verify OTP code
 */
export async function verifyOtp(data: VerifyOtpRequest): Promise<{ message: string; resetToken: string }> {
  return post(AUTH_ENDPOINTS.VERIFY_OTP, data);
}

/**
 * Validate OTP (alternative endpoint)
 */
export async function validateOtp(data: { email: string; otp: string }): Promise<{ validated: boolean; resetToken?: string }> {
  return post(AUTH_ENDPOINTS.VALIDATE_OTP, {
    emailOrPhone: data.email,
    otp: data.otp,
    method: 'email',
  });
}

/**
 * Reset password with OTP
 */
export async function resetPassword(data: ResetPasswordRequest): Promise<{ success: boolean; message: string }> {
  return post(AUTH_ENDPOINTS.RESET_PASSWORD, data);
}

/**
 * Normalize backend user response → frontend User type.
 * Backend stores phone as `phoneNumber` (+ optional `phonePrefix`),
 * but the frontend User type uses `phone`.
 */
function normalizeUser(raw: Record<string, unknown>): User {
  const phoneNumber = raw.phoneNumber as string | undefined;
  const phonePrefix = raw.phonePrefix as string | undefined;
  const phone =
    (raw.phone as string | undefined) ||
    (phonePrefix && phoneNumber ? `${phonePrefix}${phoneNumber}` : phoneNumber);

  return {
    ...(raw as unknown as User),
    image: (raw.image as string | undefined) || (raw.profilePicture as string | undefined) || undefined,
    phone: phone || undefined,
    phonePrefix: phonePrefix || undefined,
  };
}

/**
 * Get current user profile
 */
export async function getCurrentUser(): Promise<User> {
  const raw = await get<Record<string, unknown>>(AUTH_ENDPOINTS.USER_PROFILE);
  return normalizeUser(raw);
}

/**
 * Update user profile
 */
export async function updateProfile(data: Partial<User>): Promise<User> {
  const raw = await patch<Record<string, unknown>>(AUTH_ENDPOINTS.PROFILE, data);
  return normalizeUser(raw);
}

/**
 * Upload user avatar
 */
export async function uploadAvatar(file: File): Promise<{ image: string }> {
  return upload<{ image: string }>(AUTH_ENDPOINTS.PROFILE_AVATAR, file, 'file');
}

export async function updateLanguage(language: 'es' | 'en' | 'fr'): Promise<{ message?: string }> {
  // Use _skipLogout so a 401 on this non-critical endpoint never forces a logout
  const response = await apiClient.patch<{ message?: string }>(
    AUTH_ENDPOINTS.UPDATE_LANGUAGE,
    { language },
    { _skipLogout: true } as object
  );
  return response.data;
}

export async function deleteAccount(): Promise<{ message?: string }> {
  return del<{ message?: string }>(AUTH_ENDPOINTS.DELETE_ACCOUNT);
}

/**
 * Get available cargo vehicle types (no auth required)
 */
export async function getVehicleTypes(): Promise<VehicleType[]> {
  try {
    const result = await get<VehicleType[]>('/vehicle-types');
    return Array.isArray(result) ? result : [];
  } catch {
    return [];
  }
}
